import type { ContentRow } from '../types/content';
import type { StreamConfig } from '../types/stream';
import { findNextIndex } from './scheduler';
import { recordPlay } from '../sync/logSync';
import { generateQrDataUrl, qrPositionStyle, qrSizePx } from './qr';
import { getRenderer } from './renderers/registry';
import type { RenderContext, RenderSlot } from './renderers/types';
import { applyVideoVolume } from './renderers/util';
import { isTvBrowser } from '../helpers/capabilities';

export type { RenderSlot as Slot } from './renderers/types';

// Semantic player status messages (translated by the UI).
export type PlayerMessageKey = 'no_content';

const CROSSFADE_MS = 250;
const FIRST_FRAME_TIMEOUT_MS = 4000;
const STALL_RETRY_MS = 60_000;
const SKIP_DELAY_MS = 2000;

// Grace allowed for a video that runs slightly past its configured duration, so
// a clip whose real length is a touch longer (e.g. 9s configured / 9.6s footage)
// plays out fully instead of being cut right before the end. Clips that exceed
// the configured duration by more than this are still capped (a 10s file on a
// 5s slot stops at ~5s), so the subscription duration is respected.
const VIDEO_OVERRUN_GRACE_MS = 1000;

// Smart-TV / set-top browsers (Tizen, webOS, etc.) almost always expose a
// single hardware video decoder and limited RAM. Running two <video> elements
// (double-buffer crossfade) or preloading/decoding the next clip while one
// plays starves that single decoder and causes freezing/stutter. On these
// devices we fall back to single-decoder playback: one video at a time, no
// video preload, native looping for a lone clip.
const SINGLE_DECODER_UA = isTvBrowser();

// Imperative engine ported from ActivityPlayer. Orchestrates scheduling,
// double-buffered crossfades, QR overlays and logging, delegating the actual
// per-type rendering (video/image/url/...) to pluggable MediaRenderers.
export class PlaybackController {
  private slots: [RenderSlot, RenderSlot] | null = null;
  private items: ContentRow[] = [];
  private index = -1;
  private active = 0;
  private running = false;
  private config: StreamConfig | null = null;
  private audioUnlocked = false;

  private durationTimer: ReturnType<typeof setTimeout> | null = null;
  private skipTimer: ReturnType<typeof setTimeout> | null = null;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  private preloadTimer: ReturnType<typeof setTimeout> | null = null;
  private preloadIndex = -1;

  private readonly ctx: RenderContext = {
    getVolume: () => this.config?.volume ?? 0,
    isAudioUnlocked: () => this.audioUnlocked,
    firstFrameTimeoutMs: FIRST_FRAME_TIMEOUT_MS,
  };

  // UI callback. Emits a semantic key the UI can translate.
  onMessage: (key: PlayerMessageKey | null) => void = () => {};

  // Fired at a clean boundary between items (current item's window just ended,
  // before the next begins). This is the safe point to apply a pending app
  // update / reload without cutting content mid-item.
  onBoundary: () => void = () => {};

  // True when nothing is currently playing (not started, or stalled with no
  // playable content). Reloading is harmless in this state.
  isIdle(): boolean {
    return this.index === -1;
  }

  setSlots(a: RenderSlot, b: RenderSlot): void {
    this.slots = [a, b];
  }

  setConfig(config: StreamConfig): void {
    this.config = config;
    this.reapplyActiveVolume();
  }

  setAudioUnlocked(unlocked: boolean): void {
    this.audioUnlocked = unlocked;
    this.reapplyActiveVolume();
  }

  private reapplyActiveVolume(): void {
    if (!this.slots) return;
    const slot = this.slots[this.active];
    if (slot.content?.type === 'video') {
      applyVideoVolume(slot.video, this.config?.volume ?? 0, this.audioUnlocked);
    }
  }

  isLowMemory(): boolean {
    return this.config?.low_memory_mode ?? false;
  }

  // Restrict to one active video decoder: forced on smart-TV browsers and when
  // the server enables low_memory_mode. Disables the second slot / preloading.
  private singleDecoder(): boolean {
    return SINGLE_DECODER_UA || this.isLowMemory();
  }

  // (Re)load the playlist. If already running, only restart playback when we
  // are currently stalled (mirrors reloadPlaylist()).
  async load(items: ContentRow[], forceRestart = false): Promise<void> {
    const wasStalled = this.index === -1;
    this.items = items.slice().sort((x, y) => x.position - y.position);
    if (this.running && !forceRestart && !wasStalled) return;
    this.index = -1;
    this.preloadIndex = -1;
  }

  start(): void {
    if (!this.slots) return;
    this.running = true;
    void this.advance(true);
  }

  stop(): void {
    this.running = false;
    this.clearTimers();
    if (this.slots) {
      for (const slot of this.slots) this.resetSlot(slot);
    }
  }

  private clearTimers(): void {
    if (this.durationTimer) clearTimeout(this.durationTimer);
    if (this.skipTimer) clearTimeout(this.skipTimer);
    if (this.stallTimer) clearTimeout(this.stallTimer);
    if (this.preloadTimer) clearTimeout(this.preloadTimer);
    this.durationTimer = this.skipTimer = this.stallTimer = this.preloadTimer = null;
  }

  private stall(): void {
    this.index = -1;
    this.onMessage('no_content');
    if (this.stallTimer) clearTimeout(this.stallTimer);
    this.stallTimer = setTimeout(() => void this.advance(true), STALL_RETRY_MS);
  }

  private skipSoon(nextIndex: number): void {
    this.index = nextIndex;
    this.skipTimer = setTimeout(() => void this.advance(), SKIP_DELAY_MS);
  }

  // Core step: pick the next item, prepare it (reusing the preloaded slot when
  // possible), crossfade to it, then begin preloading the following item.
  private async advance(initial = false): Promise<void> {
    if (!this.running || !this.slots) return;
    this.clearTimers();

    const next = findNextIndex(this.items, this.index);
    if (next < 0) {
      this.stall();
      return;
    }

    const content = this.items[next];
    const single = this.singleDecoder();

    // A lone playable video loops natively in place: don't tear it down and
    // reload every cycle (that caused a black flash between loops). Just re-arm
    // the schedule re-check and re-log the play.
    const activeSlot = this.slots[this.active];
    if (
      !initial &&
      next === this.index &&
      content.type === 'video' &&
      activeSlot.content?.uuid === content.uuid &&
      activeSlot.video.loop
    ) {
      this.scheduleNext(content);
      return;
    }

    // In single-decoder mode reuse the active slot (one <video> only); otherwise
    // double-buffer into the idle slot for a seamless crossfade.
    const targetSlotIdx = initial || single ? this.active : 1 - this.active;
    const targetSlot = this.slots[targetSlotIdx];

    const alreadyPreloaded =
      !initial && !single && this.preloadIndex === next && targetSlot.content?.uuid === content.uuid;

    if (!alreadyPreloaded) {
      const ok = await this.prepare(targetSlot, content);
      if (!this.running) return;
      if (!ok) {
        this.skipSoon(next);
        return;
      }
    }

    this.index = next;
    this.onMessage(null);

    const renderer = getRenderer(content.type);
    if (!renderer) {
      this.skipSoon(next);
      return;
    }

    const ok = await renderer.activate(targetSlot, content, this.ctx);
    if (!this.running) return;
    if (!ok) {
      this.skipSoon(next);
      return;
    }

    // Loop natively only when this is the sole playable item, so a single clip
    // never has to be reloaded (and never shows a gap).
    if (content.type === 'video') {
      targetSlot.video.loop = findNextIndex(this.items, next) === next;
    }

    this.reveal(targetSlotIdx);
    this.active = targetSlotIdx;

    this.scheduleNext(content);

    // Preload the following item into the idle slot for an instant crossfade.
    // Skipped in single-decoder mode (no second slot) and deferred until the
    // crossfade fully finishes, so we never wipe the still-fading-out layer.
    if (!single) {
      this.preloadTimer = setTimeout(
        () => this.preloadFollowing(targetSlotIdx),
        CROSSFADE_MS + 60,
      );
    }
  }

  // Arm the timer that ends the current item's display window: log the play and
  // step to the next item (or re-check the schedule for a looping lone clip).
  private scheduleNext(content: ContentRow): void {
    const startMs = Date.now();
    this.durationTimer = setTimeout(
      () => {
        void recordPlay(content, startMs, Date.now());
        this.onBoundary();
        void this.advance();
      },
      this.displayWindowMs(content),
    );
  }

  // How long the current item should stay on screen. Static media and looping
  // lone clips use the configured duration verbatim. For a non-looping video we
  // allow a small overshoot up to VIDEO_OVERRUN_GRACE_MS so a clip that runs a
  // touch past its configured seconds finishes, while clips that are much longer
  // than the configured (subscription) duration are still capped.
  private displayWindowMs(content: ContentRow): number {
    const configuredMs = Math.max(1, content.duration) * 1000;
    if (content.type !== 'video' || !this.slots) return configuredMs;

    const video = this.slots[this.active].video;
    if (video.loop) return configuredMs;

    const intrinsic = video.duration;
    if (!Number.isFinite(intrinsic) || intrinsic <= 0) return configuredMs;

    const intrinsicMs = Math.ceil(intrinsic * 1000);
    return Math.max(configuredMs, Math.min(intrinsicMs, configuredMs + VIDEO_OVERRUN_GRACE_MS));
  }

  // Buffer the item that comes after the current one into the idle slot so the
  // next crossfade is instant. Runs only once the previous transition settled.
  private preloadFollowing(currentSlotIdx: number): void {
    if (!this.running || !this.slots) return;
    const followIndex = findNextIndex(this.items, this.index);
    if (followIndex < 0 || followIndex === this.index) return;

    const followSlot = this.slots[1 - currentSlotIdx];
    void this.prepare(followSlot, this.items[followIndex]).then((ok) => {
      if (!this.running) return;
      if (ok) this.preloadIndex = followIndex;
    });
  }

  // Load content into a hidden slot via its renderer. Returns false if the
  // content cannot be prepared (missing media, unknown type, etc.).
  private async prepare(slot: RenderSlot, content: ContentRow): Promise<boolean> {
    this.resetSlot(slot);
    slot.content = content;

    const renderer = getRenderer(content.type);
    if (!renderer) return false;

    await this.prepareQr(slot, content);

    return renderer.prepare(slot, content, this.ctx);
  }

  // Crossfade the given slot in and the other slot out.
  private reveal(slotIdx: number): void {
    if (!this.slots) return;
    const slot = this.slots[slotIdx];
    const other = this.slots[1 - slotIdx];

    slot.root.style.zIndex = '2';
    slot.root.style.opacity = '1';
    other.root.style.zIndex = '1';
    other.root.style.opacity = '0';
    this.showQr(slot);
    this.hideQr(other);

    // Quiet the faded-out content once the transition completes.
    const otherContent = other.content;
    window.setTimeout(() => {
      if (otherContent) getRenderer(otherContent.type)?.deactivate?.(other);
    }, CROSSFADE_MS + 50);
  }

  private async prepareQr(slot: RenderSlot, content: ContentRow): Promise<void> {
    if (!content.qrShow || !content.qrData) {
      slot.qr.style.display = 'none';
      slot.qr.removeAttribute('src');
      return;
    }
    const dataUrl = await generateQrDataUrl(content.qrData);
    if (!dataUrl) {
      slot.qr.style.display = 'none';
      return;
    }
    const size = qrSizePx(content.qrSize);
    for (const prop of ['top', 'bottom', 'left', 'right', 'transform']) {
      slot.qr.style.removeProperty(prop);
    }
    slot.qr.src = dataUrl;
    slot.qr.style.width = `${size}px`;
    slot.qr.style.height = `${size}px`;
    const pos = qrPositionStyle(content.qrPosition);
    for (const [prop, value] of Object.entries(pos)) {
      slot.qr.style.setProperty(prop, value);
    }
    slot.qr.style.visibility = 'hidden';
    slot.qr.style.display = 'block';
  }

  private showQr(slot: RenderSlot): void {
    if (slot.qr.getAttribute('src')) slot.qr.style.visibility = 'visible';
  }

  private hideQr(slot: RenderSlot): void {
    slot.qr.style.visibility = 'hidden';
  }

  // Generic slot reset covering every element kind a renderer may have used.
  private resetSlot(slot: RenderSlot): void {
    try {
      slot.video.pause();
    } catch {
      /* noop */
    }
    slot.video.removeAttribute('src');
    try {
      slot.video.load();
    } catch {
      /* noop */
    }
    slot.img.removeAttribute('src');
    slot.frame.removeAttribute('src');

    if (slot.objectUrl) {
      URL.revokeObjectURL(slot.objectUrl);
      slot.objectUrl = null;
    }
    slot.content = null;
  }
}
